import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { NotificationService, Notification } from '../../services/notificationService';
import { useNotificationModal } from '@/src/context/notificationModal';
import { getCategoryIcon } from '../NotificationModal/icons';
import {
  PopupContainer,
  PopupContent,
  PopupHeader,
  PopupIconWrapper,
  PopupTitle,
  PopupDescription,
  PopupActions,
  PopupButton,
  CloseButton,
  PopupMeta,
  ClientInfo,
  TimeInfo,
} from './styles';

interface NotificationPopupProps {
  notification: Notification | null;
  onClose: () => void;
  onViewAll: () => void;
}

export function NotificationPopup({ notification, onClose, onViewAll }: NotificationPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const autoCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (notification) {
      setIsVisible(true);
      
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
      
      autoCloseTimeoutRef.current = setTimeout(() => {
        handleClose();
      }, 8000);

      return () => {
        if (autoCloseTimeoutRef.current) {
          clearTimeout(autoCloseTimeoutRef.current);
        }
      };
    }
  }, [notification]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const handleViewAll = useCallback(() => {
    handleClose();
    setTimeout(() => {
      onViewAll();
    }, 300);
  }, [handleClose, onViewAll]);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}min atrás`;
    return 'Recém recebida';
  };

  if (!notification) return null;

  const popupContent = (
    <PopupContainer isVisible={isVisible} onClick={handleClose}>
      <PopupContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose} aria-label="Fechar notificação">
          ×
        </CloseButton>
        
        <PopupHeader>
          <PopupIconWrapper category={notification.category || 'system'}>
            {getCategoryIcon(notification.category || 'system')}
          </PopupIconWrapper>
          
          <div>
            <PopupTitle>{notification.title}</PopupTitle>
            <PopupDescription>
              {notification.processed_description || notification.description}
            </PopupDescription>
            
            <PopupMeta>
              {notification.client && (
                <ClientInfo>
                  {notification.client.company_name || notification.client.name}
                </ClientInfo>
              )}
              <TimeInfo>
                {formatRelativeTime(notification.created_at)}
              </TimeInfo>
            </PopupMeta>
          </div>
        </PopupHeader>

        <PopupActions>
          <PopupButton variant="secondary" onClick={handleClose}>
            Fechar
          </PopupButton>
          <PopupButton variant="primary" onClick={handleViewAll}>
            Ver todas
          </PopupButton>
        </PopupActions>
      </PopupContent>
    </PopupContainer>
  );

  return createPortal(popupContent, document.body);
}

